# Drift detection workflow

A graph is only useful as long as it matches the app. Cairn keeps the two in sync via `cairn scan` and the per-PR GitHub Action.

## The loop

```
edit app → cairn scan → review drift report → cairn accept / dismiss → commit
```

Each step in detail.

## 1. Run `cairn scan`

```bash
cairn scan
```

The scanner walks your codebase (AST-first) plus, optionally, the deployed app via a headless Playwright crawl. For every node in `.graphs/*.yaml` it asks:

- Does this route still exist?
- Does the element matching `selectorChain[0]` still exist?
- If `selectorChain[0]` is gone, do `role` + `accessibleName` + `parentRegion` match somewhere else? (Possibly renamed.)
- Does the visible text still match what's asserted?

The output is a structured drift report at `.graphs/.drift/<scan-id>.json` plus a human-readable summary printed to your terminal.

## 2. Read the report

Each drift entry has:

| Field | Meaning |
|---|---|
| `kind` | `selector-renamed`, `element-moved`, `text-changed`, `element-removed`, `new-element`, `route-changed` |
| `confidence` | 0–1; above 0.85 is auto-patchable, 0.5–0.85 is review-needed, below 0.5 is investigate |
| `flow` + `node` | The graph location of the affected element |
| `currentFingerprint` | What the graph expects |
| `proposedPatch` | The YAML edit that would resolve the drift (only present if confidence is high enough) |

In the dashboard, the same report renders under `/teams/.../projects/.../drift` with batch select + accept controls.

## 3. Accept or dismiss

To apply every high-confidence patch in the latest scan:

```bash
cairn accept --all
```

To apply a specific drift entry:

```bash
cairn accept --drift drift_abc123
```

To dismiss (mark as not-drift, e.g. an intentional rename you're aware of):

```bash
cairn accept --drift drift_abc123 --dismiss
```

Both operations write back to `.graphs/*.yaml` atomically (the canonical writer preserves comments and key order) and append an entry to `.graphs/.drift/.history.json` for audit.

## 4. Commit

The patched YAML is now in your working tree. Commit alongside the app change that caused the drift:

```bash
git add .graphs/login.yaml
git commit -m "fix(login): update Sign in selector to match new design"
```

## CI integration

When you connect a project to GitHub (Project settings → Integrations), the Cairn GitHub Action runs on every PR:

1. Checks out the PR branch
2. Runs `cairn scan --since main`
3. Posts a single sticky comment summarizing drift with a Markdown table + suggested patches
4. Fails the check (configurable: warn-by-default) when high-confidence drift goes unresolved

The PR comment is the only V1 GitHub touchpoint — auto-PR for accepted patches lands V1.5+. For now, `cairn accept` is the local-write path.

## See also

- [Schema reference](/docs/schema-reference) for the `fingerprint:` structure that drift detection inspects
- [CLI reference](/docs/cli-reference) for the full `scan` / `accept` flag set
